import { Box, Button, Checkbox, Grid, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Dialog from 'components/dialog';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { AlertTriangle, Info, Search, Square, SquareCheckBig } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import _, { isEmpty } from 'underscore';

import { BestMixFeedListItem } from 'views/feed-management/features/best-mix-feeds/types/best-mix-feed-list-item.type';

import Breadcrumbs from 'components/breadcrumbs';
import CheckboxSelectItem from 'components/checkbox-select-item';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';

import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import FeedManagementTabsHeader from '../../components/feed-management-tabs-header';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import ClearSelection from 'components/clear-selection';
import Paper from 'components/paper';
import Select from 'components/select';
import Switch from 'components/switch';
import { usePagination } from 'hooks/use-pagination';
import { getFeedManagementBestMixFeeds } from 'views/feed-management/features/best-mix-feeds/api/get-best-mix-feeds';
import { putBestMixFeedStatus } from 'views/feed-management/features/best-mix-feeds/api/update-best-mix-feeds';
import { UpdateBestMixFeedStatusInput } from 'views/feed-management/features/best-mix-feeds/types/update-best-mix-feed-status-input.type';
import { getFeedManagementCustomers } from 'views/feed-management/features/customers/api/get-customers';
import { putCustomerLinkProducts } from 'views/feed-management/features/customers/api/update-customers';
import { UpdateCustomerLinkFeedsInput } from 'views/feed-management/features/customers/types/update-customer-link-feeds-input.type';
import { getFeedManagementUserPlants } from 'views/feed-management/features/plants/api/get-plants';

type Props = {};

const ListBestMixFeeds: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_NAME_ID = 'feedManagement.listBestMixFeeds';
  const PAGINATION_LIMIT = 10;

  // STATE
  const [products, setProducts] = useState<BestMixFeedListItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [active, setActive] = useState<boolean>(true);
  const [selectAllFeeds, setSelectAllFeeds] = useState<Record<number, boolean>>({});
  const [selectedFeeds, setSelectedFeeds] = useState<string[]>([]);
  const customerLinkDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const manageFeedsDialogRef = useRef<React.ElementRef<typeof Dialog>>();
  const [plantSearch, setPlantSearch] = useState<string>('');
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['active']);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // FETCH DATA
  const { data: bestMixFeeds, isFetching } = useGet(['getFeedManagementBestMixFeeds'], () => getFeedManagementBestMixFeeds());

  useEffect(() => {
    if (!bestMixFeeds) return;

    setProducts(
      _.chain(bestMixFeeds)
        .sortBy((e) => e.description.toLowerCase())
        .value()
    );
  }, [bestMixFeeds]);

  const { data: plants } = useGet(['getFeedManagementUserPlants'], () => getFeedManagementUserPlants());

  const { data: customers } = useGet(['getFeedManagementCustomers'], () => getFeedManagementCustomers());

  const getPlantCodes = useMemo(() => plants?.map((p) => p.code) || [], [plants]);

  const [selectedPlantCodes, setSelectedPlantCodes] = useState<string[]>([]);

  // POST DATA
  const updateBestmixFeedsStatus = usePost<AxiosResponse, { globalCompanyId: string; productCodes: UpdateBestMixFeedStatusInput }>(
    (request) => putBestMixFeedStatus(request.globalCompanyId, request.productCodes),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      manageFeedsDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementBestMixFeeds']
      });
      resetStates();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateLinkCustomerFeed = usePost<AxiosResponse, { data: UpdateCustomerLinkFeedsInput }>(
    (request) => putCustomerLinkProducts(request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      customerLinkDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementBestMixFeeds']
      });
      resetStates();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const customerList = useMemo(() => new Map(customers?.map((item) => [item.id, item])), [customers]);

  const customerIds = useMemo(() => {
    return Array.from(customerList.keys());
  }, [customerList]);

  const customer = useMemo(() => Array.from(customerList, ([key, value]) => value), [customerList]);

  const customerMap = customer
    .filter((s) => s.name.toLowerCase().includes(customerSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  // HANDLERS
  const handleCheckAllCustomersChange = () => {
    setSelectedCustomerIds((prev) => (prev.length === customer?.length ? [] : customerIds));
  };

  const handleCustomerItemClick = (index: number) => {
    const customerId = customerMap[index].id;
    const isSelected = selectedCustomerIds.includes(customerId);
    if (isSelected) {
      setSelectedCustomerIds(selectedCustomerIds.filter((id) => id !== customerId));
    } else {
      setSelectedCustomerIds([...selectedCustomerIds, customerId]);
    }
  };

  const handleBestmixFeedsAction = () => {
    const codes: UpdateBestMixFeedStatusInput = {
      productCodes: selectedFeeds,
      action: active ? 'inactivate' : 'activate',
    };
    updateBestmixFeedsStatus.mutate({ globalCompanyId: account.company!.companyId, productCodes: codes });
  };

  const handleCustomerLinkFeeds = () => {
    const request: UpdateCustomerLinkFeedsInput = {
      globalCompanyIds: selectedCustomerIds,
      productCodes: selectedFeeds,
    };
    updateLinkCustomerFeed.mutate({ data: request });
  };

  const handleCheckAllChange = () => {
    setPlantSearch('');
    setSelectedPlantCodes((prev) => (prev.length === plants?.length ? [] : getPlantCodes));
  };

  const handleActive = () => {
    handlePagination(1);
    setActive((prev) => !prev);
    handleClearSelectedFeeds();
  };

  const resetStates = () => {
    setSelectAllFeeds({});
    setSelectedFeeds([]);
    setSelectedCustomerIds([]);
  };

  const handleSelectAllFeeds = (pageNumber: number, checked: boolean) => {
    const feedsOnPage = filteredRows.slice(from, to);
    const allFeedsIdsOnPage = feedsOnPage.map((product) => product.productCode);
    const updatedSelectedFeeds = checked
      ? [...selectedFeeds, ...allFeedsIdsOnPage]
      : selectedFeeds.filter((id) => !allFeedsIdsOnPage.includes(id));
    setSelectedFeeds(updatedSelectedFeeds);
    setSelectAllFeeds((prevSelectAllFeeds) => ({ ...prevSelectAllFeeds, [pageNumber]: checked }));
  };

  const handleClearSelectedFeeds = () => {
    setSelectedFeeds([]);
    setSelectAllFeeds({});
  };

  const handleSelectFeeds = (value: string, checked: boolean) => {
    setSelectedFeeds(checked ? selectedFeeds.concat([value]) : selectedFeeds.filter((s) => s !== value));
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // RENDERS
  const renderSelectedFeeds = (selectedFeeds: string[]) => {
    const filteredProducts = products?.filter((u) => selectedFeeds.includes(u.productCode));
    return (
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} wrap="wrap" overflow={'auto'} maxHeight={'300px'}>
        {filteredProducts.map((product) => (
          <Grid size={6} key={`${product.productCode}`}>
            <ListItem sx={{ py: 1, px: 2, borderBottom: '1px solid #ddd' }}>
              <Typography variant="body1">{product?.description}</Typography>
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  const filteredRows = useMemo(() => {
    return _.chain(products)
      .filter((item) => item.scenarioActive === active)
      .filter((item) => (isEmpty(selectedPlantCodes) ? false : selectedPlantCodes.includes(item.plantCode)))
      .filter((item) => {
        if (!selectedFilters.length) return true;
        else {
          if (selectedFilters.includes('active') && item.active) return true;
          if (selectedFilters.includes('archive') && item.archive) return true;
          if (selectedFilters.includes('hasMandatoryNutrients') && item.hasMandatoryNutrients) return true;
          return true;
        }
      })
      .filter((item) =>
        isEmpty(searchValue)
          ? true
          : item.code.toLowerCase().includes(searchValue.toLowerCase()) ||
            item.description.toLowerCase().includes(searchValue.toLowerCase())
      )
      .value();
  }, [products, selectedPlantCodes, selectedFilters, searchValue, active, selectedPlantCodes]);

  useEffect(() => {
    handlePagination(1);
  }, [filteredRows]);

  useEffect(() => {
    setSelectedPlantCodes(getPlantCodes);
  }, [getPlantCodes]);

  return (
    <>
      <FeedManagementTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={<Breadcrumbs />}
      />
      <Paper transparent>
        <Grid spacing={4}>
          <Grid>
            <FilterSidebar
              clearFilters={() => {
                setSelectedPlantCodes(getPlantCodes);
                setSelectedFilters(['active']);
                setSearchValue('');
                setActive(true);
                handleClearSelectedFeeds();
              }}
              showClearFilter={
                selectedPlantCodes.length !== getPlantCodes.length ||
                (selectedFilters.length > 1 && !selectedFilters.includes('active')) ||
                !active
              }>
              {active && (
                <Dialog
                  maxWidth="md"
                  onClose={() => {
                    setCustomerSearch('');
                    setSelectedCustomerIds([]);
                  }}
                  ref={customerLinkDialogRef}
                  actions={
                    <Button
                      sx={{ margin: theme.spacing(0, 4, 4, 0) }}
                      type="submit"
                      onClick={handleCustomerLinkFeeds}
                      disabled={updateLinkCustomerFeed.isPending || selectedCustomerIds.length === 0}>
                      <FormattedMessage id={`general.save`} />
                    </Button>
                  }
                  title={intl.formatMessage({ id: `${PAGE_NAME_ID}.linkToCustomer` })}
                  button={
                    <Button
                      fullWidth={isMobile}
                      disabled={!selectAllFeeds[currentPage] && selectedFeeds.length === 0}
                      size={isMobile ? 'small' : 'medium'}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.linkToCustomer`} />
                    </Button>
                  }>
                  <Box>
                    <Select
                      multiple
                      dark
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.customerSelect` })}
                      value={selectedCustomerIds}
                      MenuProps={{
                        autoFocus: false,
                      }}
                      onChange={(e) => setSelectedCustomerIds(e.target.value as string[])}
                      search={{
                        value: customerSearch,
                        onChange: setCustomerSearch,
                      }}
                      renderValue={() => {
                        const selectedCustomers = selectedCustomerIds.map((id) => {
                          return customerMap.find((customer) => customer.id === id)?.name ?? '';
                        });
                        return selectedCustomers.join(', ');
                      }}
                      checkAll={{
                        value: selectedCustomerIds.length === customerIds.length,
                        onChange: handleCheckAllCustomersChange,
                      }}>
                      <FixedSizeList
                        height={500}
                        width={500}
                        itemSize={45}
                        itemCount={
                          customerMap.filter((customer) => {
                            return customer.name.toLowerCase().includes(customerSearch.toLowerCase());
                          }).length
                        }>
                        {({ index, style }) => {
                          const filteredCustomerMap = customerMap.filter((customer) => {
                            return customer.name.toLowerCase().includes(customerSearch.toLowerCase());
                          });
                          const customer = filteredCustomerMap[index];
                          const actualIndex = customerMap.findIndex((c) => c.id === customer.id);

                          return (
                            <Box key={index} style={style} onClick={() => handleCustomerItemClick(actualIndex)}>
                              <CheckboxSelectItem key={index} checked={selectedCustomerIds.includes(customer.id)} value={customer.id}>
                                {customer.name}
                              </CheckboxSelectItem>
                            </Box>
                          );
                        }}
                      </FixedSizeList>
                    </Select>
                    <Typography pb={5} pt={5}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.dialog.linkToSelectedCustomers`} />
                    </Typography>
                    <Box>{renderSelectedFeeds(selectedFeeds)}</Box>
                  </Box>
                </Dialog>
              )}

              {!!filteredRows.length && (
                <Dialog
                  maxWidth="md"
                  ref={(el) => {
                    if (el) manageFeedsDialogRef.current = el;
                  }}
                  actions={
                    <Button
                      type="submit"
                      size="small"
                      color="secondary"
                      onClick={handleBestmixFeedsAction}
                      disabled={updateBestmixFeedsStatus.isPending}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.dialog.${active ? 'deactivate' : 'activate'}`} />
                    </Button>
                  }
                  title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.${active ? 'deactivateTitle' : 'activateTitle'}` })}
                  button={
                    <Button
                      fullWidth={isMobile}
                      disabled={!selectAllFeeds[currentPage] && selectedFeeds.length === 0}
                      size={isMobile ? 'small' : 'medium'}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.${active ? 'deactivateFeeds' : 'activateFeeds'}`} />
                    </Button>
                  }>
                  <Box>
                    <Typography pb={5} pt={5}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.dialog.${active ? 'deactivateDialog' : 'activateDialog'}`} />
                    </Typography>
                    <Box>{renderSelectedFeeds(selectedFeeds)}</Box>
                  </Box>
                </Dialog>
              )}

              {!!plants && (
                <Select
                  multiple
                  onOpen={() => {
                    setPlantSearch('');
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filter.plantLabel` })}
                  value={selectedPlantCodes}
                  onChange={(event) => {
                    const plantsIds = event.target.value as string[];
                    setSelectedPlantCodes(plantsIds);
                  }}
                  checkAll={{
                    value: selectedPlantCodes.length === plants?.length,
                    onChange: handleCheckAllChange,
                  }}
                  search={{ value: plantSearch, onChange: setPlantSearch }}>
                  {_.chain(plants)
                    .filter((p) => p.name.toLowerCase().includes(plantSearch.toLowerCase()))
                    .sortBy((p) => p.name)
                    .value()
                    ?.map((plant) => (
                      <CheckboxSelectItem checked={selectedPlantCodes.includes(plant.code)} value={plant.code} key={`${plant.code}`}>
                        {plant.name}
                      </CheckboxSelectItem>
                    ))}
                </Select>
              )}
              <Select
                multiple
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filter.statusLabel` })}
                value={selectedFilters}
                onChange={(event) => {
                  setSelectedFilters(event.target.value as string[]);
                }}>
                <CheckboxSelectItem checked={selectedFilters.includes('active')} value={'active'}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filter.options.active`} />
                </CheckboxSelectItem>
                <CheckboxSelectItem checked={selectedFilters.includes('archive')} value={'archive'}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filter.options.temporary`} />
                </CheckboxSelectItem>
                <CheckboxSelectItem checked={selectedFilters.includes('hasMandatoryNutrients')} value={'hasMandatoryNutrients'}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filter.options.hasMandatoryNutrients`} />
                </CheckboxSelectItem>
              </Select>

              <TextField
                label={intl.formatMessage({ id: 'general.search' })}
                value={searchValue}
                endAdornment={<Search size={18} />}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                }}
              />
              <Box display={'flex'} width={'100%'} justifyContent={'end'}>
                <Switch
                  sx={{ justifyContent: 'center' }}
                  label={
                    active
                      ? intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeLabel` })
                      : intl.formatMessage({ id: `${PAGE_NAME_ID}.list.inactiveLabel` })
                  }
                  value={active}
                  onChange={() => handleActive()}
                />
              </Box>
            </FilterSidebar>
          </Grid>
          <Grid order={{ xs: 2, md: 1 }}>
            {isFetching ? (
              <LoadingSpinner />
            ) : (
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                  {!!filteredRows.length && (
                    <Switch
                      sx={{ justifyContent: 'center' }}
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectAllFeeds` })}
                      value={selectAllFeeds[currentPage] || false}
                      onChange={(event) => handleSelectAllFeeds(currentPage, event.target.checked)}
                    />
                  )}
                  {!!selectedFeeds.length && <ClearSelection onClick={handleClearSelectedFeeds} />}
                </Box>
                <List
                  options={[
                    { id: 'warning', label: '', autoWidth: true, align: 'center' },
                    { id: 'checkbox', label: '', autoWidth: true },
                    { id: 'description', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
                    { id: 'productNumber', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.numberLabel` }), align: 'right' },
                    { id: 'updatedDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.updatedDateLabel` }), align: 'right' },
                    { id: 'actions', label: '', align: 'center', hide: !isMobile },
                  ]}
                  orderBy={'description'}
                  rows={products}
                  sort={setProducts}
                  isLoading={isFetching}
                  noResult={{
                    show: filteredRows.length <= 0,
                    text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
                  }}>
                  {filteredRows.slice(from, to).map((row) => (
                    <ListRow key={row.code} onClick={() => handleOnClick(`/feed-management/bestmix-feeds/${row.code}`)}>
                      <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                        <Checkbox
                          disableRipple
                          color="primary"
                          tabIndex={-1}
                          checked={selectedFeeds.includes(row.productCode)}
                          icon={<Square size={22} color={theme.palette.darkBlue.main} />}
                          checkedIcon={<SquareCheckBig size={22} color={theme.palette.darkBlue.main} />}
                          onChange={(_, checked) => handleSelectFeeds(row.productCode, checked)}
                        />
                      </ListCell>
                      <ListCell>
                        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={4}>
                          {!row.hasNutrientValuesCalculated && (
                            <Tooltip arrow title={intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.product` })}>
                              <AlertTriangle color={theme.palette.warning.light} size={18} />
                            </Tooltip>
                          )}
                          {row.hasNutrientValuesCalculated && row.hasIncorrectBestMixNutrientValues && (
                            <Tooltip arrow title={intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltip.incorrectValues` })}>
                              <Info size={18} />
                            </Tooltip>
                          )}
                        </Box>
                      </ListCell>
                      <ListCell sx={{ whiteSpace: 'break-spaces !important' }}>{row.description}</ListCell>
                      <ListCell align="right">
                        {row.code} / {row.productNumber}
                      </ListCell>
                      <ListCell align="right">
                        <FormattedDate value={row.updatedDate} day="2-digit" month="short" year="numeric" />
                      </ListCell>
                      <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="flex-start"
                          paddingTop={isMobile ? 4 : 0}
                          width={'100%'}>
                          <Button
                            fullWidth={isMobile}
                            size="small"
                            onClick={() => handleOnClick(`/feed-management/bestmix-feeds/${row.code}`)}>
                            <FormattedMessage id="general.edit" values={{ type: '' }} />
                          </Button>
                        </Box>
                      </ListCell>
                    </ListRow>
                  ))}
                </List>

                <Pagination
                  currentPage={currentPage}
                  pages={Math.ceil(filteredRows.length / PAGINATION_LIMIT)}
                  onClick={handlePagination}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default ListBestMixFeeds;
